import { CommonModule } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltip } from "@angular/material/tooltip";
import { requireOneOf } from "@directives/require-one-of.directive";
import { emailLengthValidator } from "@directives/user-admin/email-validator.directive";
import { OrganizationContact } from "@models/organizationContact.model";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ModalStateService } from "@services/modal-state.service";
import { ContactDetailsActions } from "@store/actions/contact-details/contact-details.actions";
import { selectIsContactSaveDisabled } from "@store/selectors/contact-details/contact-details.selectors";
import { selectOrgId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { Subscription } from "rxjs";

@Component({
  selector: "app-edit-add-org-contact-modal",
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDividerModule,
    MatSelectModule,
    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatTooltip,
  ],
  templateUrl: "./edit-add-org-contact-modal.component.html",
  styleUrl: "./edit-add-org-contact-modal.component.css",
})
export class EditAddOrganizationContactModalComponent
  implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy
{
  readonly dialogRef = inject(
    MatDialogRef<EditAddOrganizationContactModalComponent>
  );
  readonly data = inject(MAT_DIALOG_DATA);
  readonly modalStateService = inject(ModalStateService);
  private readonly store = inject(Store);
  private readonly updates$ = inject(Actions);
  private readonly cdr = inject(ChangeDetectorRef);

  orgId$ = this.store.select(selectOrgId);
  orgId = toSignal(this.orgId$);
  isContactSaveDisabled$ = this.store.select(selectIsContactSaveDisabled);
  hasSaveButtonBeenPressed = false;

  subs: Subscription[] = [];

  editAddContactForm = new FormGroup({
    name: new FormControl(this.data.contact?.name ?? "", {
      validators: [Validators.required, Validators.maxLength(128)],
    }),
    role: new FormControl(this.data.contact?.role ?? "", {
      validators: [Validators.maxLength(64)],
    }),
    emailAddress: new FormControl(this.data.contact?.emailAddress ?? "", {
      validators: [
        Validators.pattern(
          /^[\w]{1,}[\w.+-]{0,}@[\w-]{1,}([.][a-zA-Z]{2,}|[.][\w-]{2,}[.][a-zA-Z]{2,})$/
        ),
        emailLengthValidator(),
        requireOneOf(["emailAddress", "phoneNumber"]),
      ],
    }),
    phoneNumber: new FormControl(this.data.contact?.phoneNumber ?? "", {
      validators: [
        Validators.maxLength(32),
        requireOneOf(["emailAddress", "phoneNumber"]),
      ],
    }),
  });

  ngOnInit(): void {

    this.modalStateService.setModalOpen(true);
    this.subs.push(
      this.updates$
        .pipe(
          ofType(
            ContactDetailsActions.contactSavedSuccess,
            ContactDetailsActions.editContactSuccess
          )
        )
        .subscribe((payload) => {
          this.dialogRef.close();
        })
    );

    this.subs.push(
      this.editAddContactForm?.valueChanges.subscribe(() => {
        const fieldsToUpdate = ["emailAddress", "phoneNumber"];

        fieldsToUpdate.forEach((fieldName) => {
          const control = this.editAddContactForm?.get(fieldName);
          if (control) {
            control.updateValueAndValidity({ emitEvent: false });
          }
        });
      })
    );
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  submitContact() {
    if (this.hasSaveButtonBeenPressed || this.editAddContactForm.invalid) {
      return;
    }
    this.hasSaveButtonBeenPressed = true;
    if (this.data.contact) {
      var updatedContact: OrganizationContact = {
        id: this.data.contact.id,
        organizationId: this.data.contact.orgId,
        name: this.editAddContactForm.get("name")?.value ?? "",
        role: this.editAddContactForm.get("role")?.value ?? "",
        emailAddress: this.editAddContactForm.get("emailAddress")?.value ?? "",
        phoneNumber: this.editAddContactForm.get("phoneNumber")?.value ?? "",
      };
      this.store.dispatch(
        ContactDetailsActions.editContact({
          data: updatedContact,
        })
      );
    } else {
      var newContact: OrganizationContact = {
        organizationId: this.orgId() ?? "",
        name: this.editAddContactForm.get("name")?.value ?? "",
        role: this.editAddContactForm.get("role")?.value ?? "",
        emailAddress: this.editAddContactForm.get("emailAddress")?.value ?? "",
        phoneNumber: this.editAddContactForm.get("phoneNumber")?.value ?? "",
      };

      this.store.dispatch(
        ContactDetailsActions.addContactSave({
          data: newContact,
        })
      );
    }

    setTimeout(() => {
      this.hasSaveButtonBeenPressed = false;
    }, 3000);
  }

  ngOnDestroy(): void {
    this.modalStateService.setModalOpen(false);
    this.subs.forEach((sub) => {
      sub?.unsubscribe();
    });
  }
}
